import * as THREE from 'three';
import { SceneModule, SceneContext, EnvironmentState } from './types';
import { disposeHierarchy } from '../engine/disposal';
import { QualityConfig, QUALITY_CONFIGS } from '../config/quality';
import { store } from '../app/store';

export class SceneManager {
  public sceneContainer: THREE.Group = new THREE.Group();
  public currentScene: SceneModule | null = null;
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.PerspectiveCamera;
  private sceneRegistry: Map<string, () => Promise<SceneModule>> = new Map();
  private showToastFn: (msg: string) => void;
  public getBoatMoveVector?: () => THREE.Vector2;

  constructor(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera, showToast: (msg: string) => void) {
    this.renderer = renderer;
    this.camera = camera;
    this.showToastFn = showToast;
    this.sceneContainer.name = 'SceneManagerRoot';
  }

  public register(id: string, factory: () => Promise<SceneModule>): void {
    this.sceneRegistry.set(id, factory);
  }

  public async switchScene(
    sceneId: string,
    env: EnvironmentState,
    onProgress: (p: number) => void
  ): Promise<SceneModule> {
    // 1. Dispose current active scene fully
    if (this.currentScene) {
      try {
        this.currentScene.dispose();
      } catch (err) {
        console.error('Error disposing scene:', err);
      }
      disposeHierarchy(this.sceneContainer);
      this.currentScene = null;
    }

    onProgress(0.2);

    // 2. Load requested scene factory
    const factory = this.sceneRegistry.get(sceneId);
    if (!factory) {
      throw new Error(`Scene not found in registry: ${sceneId}`);
    }

    onProgress(0.4);
    const sceneModule = await factory();
    onProgress(0.6);

    const qualityPreset = store.getState().quality;
    const qualityKey = (qualityPreset === 'auto' ? 'medium' : qualityPreset) as keyof typeof QUALITY_CONFIGS;
    const qualityConfig: QualityConfig = QUALITY_CONFIGS[qualityKey] || QUALITY_CONFIGS.medium;

    const ctx: SceneContext = {
      renderer: this.renderer,
      sceneRoot: this.sceneContainer,
      camera: this.camera,
      quality: qualityConfig,
      env,
      showToast: this.showToastFn,
      getBoatMoveVector: this.getBoatMoveVector,
    };

    await sceneModule.load(ctx, (p) => {
      onProgress(0.6 + p * 0.4);
    });

    this.currentScene = sceneModule;
    store.setState({ sceneId });
    onProgress(1.0);

    return sceneModule;
  }

  public update(dt: number, env: EnvironmentState): void {
    if (this.currentScene) {
      this.currentScene.update(dt, env);
    }
  }

  public dispose(): void {
    if (this.currentScene) {
      this.currentScene.dispose();
      this.currentScene = null;
    }
    disposeHierarchy(this.sceneContainer);
  }
}
